/**
 * Настройки нарядов-допусков компании (КЧ-05): виды работ, удостоверения бригады, согласующий ОТ, анализ воздуха,
 * компоненты воздуха с допустимыми значениями, сроки анализа и наибольший срок наряда. Видят все сотрудники, меняет
 * тот, у кого право вести проверки по всей компании.
 */
import {
  CREDENTIAL_KINDS,
  type CredentialKind,
  type GasNorm,
  PERMIT_KINDS,
  type PermitKind,
  type PermitKindRule,
  type PermitSettingsFull,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Checkbox, Field, Input, Textarea } from '../../ui/form';

export type PermitSettingsView = PermitSettingsFull & { canEdit: boolean };

export function usePermitSettings() {
  return useQuery({
    queryKey: ['qa-permit-settings'],
    queryFn: () => api<PermitSettingsView>('/quality/permits/settings'),
    staleTime: 60_000,
  });
}

type GasRow = { component: string; unit: string; min: string; max: string };
const toText = (n: number | null) => (n === null ? '' : String(n).replace('.', ','));
const toNum = (v: string) => (v.trim() === '' ? null : Number(v.trim().replace(',', '.')));

export function PermitSettingsDialog({ onClose }: { onClose: () => void }) {
  const q = usePermitSettings();
  return (
    <Dialog
      open
      width="lg"
      onOpenChange={(v) => !v && onClose()}
      title="Настройки нарядов-допусков"
      description="Значения по умолчанию — ориентир: сверьте их с производственной инструкцией компании и нормативами воздуха рабочей зоны."
    >
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorBox error={q.error} />
      ) : (
        <Form s={q.data} onClose={onClose} />
      )}
    </Dialog>
  );
}

function Form({ s, onClose }: { s: PermitSettingsView; onClose: () => void }) {
  const qc = useQueryClient();
  const ro = !s.canEdit;
  const [kinds, setKinds] = useState(s.kinds);
  const [gas, setGas] = useState<GasRow[]>(
    s.gas.map((g) => ({ component: g.component, unit: g.unit, min: toText(g.min), max: toText(g.max) })),
  );
  const [mins, setMins] = useState({
    gasBeforeMinutes: String(s.gasBeforeMinutes),
    gasRepeatMinutes: String(s.gasRepeatMinutes),
    maxDays: String(s.maxDays),
  });
  const [methods, setMethods] = useState(s.isolationMethods.join('\n'));
  const [limits, setLimits] = useState({
    medium: String(s.riskLimits.medium),
    high: String(s.riskLimits.high),
    unacceptable: String(s.riskLimits.unacceptable),
  });
  const setKind = (k: PermitKind, patch: Partial<PermitKindRule>) =>
    setKinds((x) => ({ ...x, [k]: { ...x[k], ...patch } }));
  const save = useMutation({
    mutationFn: () => {
      const norms: GasNorm[] = gas
        .filter((g) => g.component.trim())
        .map((g) => ({
          component: g.component.trim(),
          unit: g.unit.trim(),
          min: toNum(g.min),
          max: toNum(g.max),
        }));
      if (
        norms.some(
          (g) => (g.min !== null && !Number.isFinite(g.min)) || (g.max !== null && !Number.isFinite(g.max)),
        )
      )
        throw new Error('Допустимые значения — числа, например 0,5');
      if (Object.values(mins).some((v) => !/^\d+$/.test(v.trim())))
        throw new Error('Сроки — целые числа: минуты и дни (0 в повторе — повтор не нужен)');
      if (Object.values(limits).some((v) => !/^\d+$/.test(v.trim())))
        throw new Error('Границы уровней риска — целые числа от 2 до 25');
      return api<PermitSettingsView>('/quality/permits/settings', {
        method: 'PUT',
        body: {
          kinds,
          gas: norms,
          gasBeforeMinutes: Number(mins.gasBeforeMinutes),
          gasRepeatMinutes: Number(mins.gasRepeatMinutes),
          maxDays: Number(mins.maxDays),
          isolationMethods: methods
            .split('\n')
            .map((m) => m.trim())
            .filter(Boolean),
          riskLimits: {
            medium: Number(limits.medium),
            high: Number(limits.high),
            unacceptable: Number(limits.unacceptable),
          },
        },
      });
    },
    onSuccess: (r) => {
      qc.setQueryData(['qa-permit-settings'], r);
      qc.invalidateQueries({ queryKey: ['qa-permit'] });
      toast('Настройки нарядов сохранены');
      onClose();
    },
  });
  return (
    <div className="flex flex-col gap-6">
      {ro ? (
        <p className="text-[14px] text-ink-2">
          Менять настройки может тот, кто ведёт проверки по всей компании.
        </p>
      ) : null}
      <section className="flex flex-col gap-3">
        <h3 className="text-[15px] font-semibold text-ink">Виды работ</h3>
        {(Object.keys(PERMIT_KINDS) as PermitKind[]).map((k) => {
          const r = kinds[k];
          return (
            <fieldset key={k} className="flex flex-col gap-3 rounded-md border border-line p-3" disabled={ro}>
              <legend className="px-1 text-[14px] font-medium text-ink">{PERMIT_KINDS[k].title}</legend>
              <div className="flex flex-wrap gap-x-5 gap-y-3">
                <Checkbox
                  className="min-h-11 md:min-h-0"
                  label="Оформляется нарядом"
                  checked={r.enabled}
                  onChange={(e) => setKind(k, { enabled: e.target.checked })}
                />
                <Checkbox
                  className="min-h-11 md:min-h-0"
                  label="Согласует охрана труда"
                  checked={r.approver}
                  onChange={(e) => setKind(k, { approver: e.target.checked })}
                />
                <Checkbox
                  className="min-h-11 md:min-h-0"
                  label="Анализ воздуха"
                  checked={r.airControl}
                  onChange={(e) => setKind(k, { airControl: e.target.checked })}
                />
                <Checkbox
                  className="min-h-11 md:min-h-0"
                  label="Изоляции LOTO"
                  checked={r.loto}
                  onChange={(e) => setKind(k, { loto: e.target.checked })}
                />
                <Checkbox
                  className="min-h-11 md:min-h-0"
                  label="Даёт огонь или искру"
                  checked={r.ignition}
                  onChange={(e) => setKind(k, { ignition: e.target.checked })}
                />
                <Checkbox
                  className="min-h-11 md:min-h-0"
                  label="Анализ рисков JSA"
                  checked={r.jsa}
                  onChange={(e) => setKind(k, { jsa: e.target.checked })}
                />
              </div>
              <Field label="Бригада не меньше, чел." className="max-w-48">
                <Input
                  className="h-11 md:h-10"
                  inputMode="numeric"
                  value={String(r.minCrew)}
                  onChange={(e) => setKind(k, { minCrew: Math.max(1, Number(e.target.value) || 1) })}
                />
              </Field>
              <div className="flex flex-col gap-1.5">
                <span className="text-[13px] font-medium text-ink">Удостоверения у каждого в бригаде</span>
                <div className="flex flex-wrap gap-x-5 gap-y-2">
                  {(Object.keys(CREDENTIAL_KINDS) as CredentialKind[]).map((c) => (
                    <Checkbox
                      key={c}
                      className="min-h-11 md:min-h-0"
                      label={CREDENTIAL_KINDS[c]}
                      checked={r.requires.includes(c)}
                      onChange={(e) =>
                        setKind(k, {
                          requires: e.target.checked ? [...r.requires, c] : r.requires.filter((x) => x !== c),
                        })
                      }
                    />
                  ))}
                </div>
              </div>
            </fieldset>
          );
        })}
      </section>
      <section className="flex flex-col gap-3">
        <h3 className="text-[15px] font-semibold text-ink">Анализ воздуха</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Field label="Перед началом — не старше, мин" htmlFor="ps-before">
            <Input
              id="ps-before"
              className="h-11 md:h-10"
              inputMode="numeric"
              disabled={ro}
              value={mins.gasBeforeMinutes}
              onChange={(e) => setMins((m) => ({ ...m, gasBeforeMinutes: e.target.value }))}
            />
          </Field>
          <Field label="Повторять в ходе работ, мин" hint="0 — не требуется" htmlFor="ps-repeat">
            <Input
              id="ps-repeat"
              className="h-11 md:h-10"
              inputMode="numeric"
              disabled={ro}
              value={mins.gasRepeatMinutes}
              onChange={(e) => setMins((m) => ({ ...m, gasRepeatMinutes: e.target.value }))}
            />
          </Field>
          <Field label="Срок наряда — не больше, дн." htmlFor="ps-days">
            <Input
              id="ps-days"
              className="h-11 md:h-10"
              inputMode="numeric"
              disabled={ro}
              value={mins.maxDays}
              onChange={(e) => setMins((m) => ({ ...m, maxDays: e.target.value }))}
            />
          </Field>
        </div>
        <ul className="flex flex-col gap-3">
          {gas.map((g, i) => (
            <li
              // biome-ignore lint/suspicious/noArrayIndexKey: строки редактируются на месте, своего ключа нет
              key={i}
              className="grid grid-cols-2 gap-2 rounded-md border border-line p-3 sm:grid-cols-[2fr_1fr_1fr_1fr_auto]"
            >
              {(['component', 'unit', 'min', 'max'] as const).map((f) => (
                <Field
                  key={f}
                  label={
                    f === 'component'
                      ? 'Компонент'
                      : f === 'unit'
                        ? 'Единица'
                        : f === 'min'
                          ? 'Не менее'
                          : 'Не более'
                  }
                  className={f === 'component' ? 'col-span-2 sm:col-span-1' : undefined}
                >
                  <Input
                    className="h-11 md:h-10"
                    disabled={ro}
                    inputMode={f === 'min' || f === 'max' ? 'decimal' : undefined}
                    value={g[f]}
                    onChange={(e) =>
                      setGas((x) => x.map((r, j) => (j === i ? { ...r, [f]: e.target.value } : r)))
                    }
                  />
                </Field>
              ))}
              {ro ? null : (
                <div className="col-span-2 flex items-end sm:col-span-1">
                  <Button
                    variant="ghost"
                    className="h-11 md:h-10"
                    onClick={() => setGas((x) => x.filter((_, j) => j !== i))}
                  >
                    Убрать
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
        {ro ? null : (
          <div>
            <Button
              variant="secondary"
              className="h-11 md:h-10"
              onClick={() => setGas((x) => [...x, { component: '', unit: '', min: '', max: '' }])}
            >
              Добавить компонент
            </Button>
          </div>
        )}
      </section>
      <section className="flex flex-col gap-3">
        <h3 className="text-[15px] font-semibold text-ink">Изоляции LOTO</h3>
        <Field label="Способы изоляции — по одному в строке" htmlFor="ps-methods">
          <Textarea
            id="ps-methods"
            rows={5}
            disabled={ro}
            value={methods}
            onChange={(e) => setMethods(e.target.value)}
          />
        </Field>
      </section>
      <section className="flex flex-col gap-3">
        <h3 className="text-[15px] font-semibold text-ink">Анализ рисков JSA</h3>
        <p className="text-[14px] text-ink-2">
          Риск = тяжесть × вероятность, по шкале 1–5 каждая (от 1 до 25). С остаточным риском от
          «недопустимого» наряд не отправляется на подпись.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {(
            [
              ['medium', 'Средний — от'],
              ['high', 'Высокий — от'],
              ['unacceptable', 'Недопустимый — от'],
            ] as const
          ).map(([f, label]) => (
            <Field key={f} label={label} htmlFor={`ps-risk-${f}`}>
              <Input
                id={`ps-risk-${f}`}
                className="h-11 md:h-10"
                inputMode="numeric"
                disabled={ro}
                value={limits[f]}
                onChange={(e) => setLimits((m) => ({ ...m, [f]: e.target.value }))}
              />
            </Field>
          ))}
        </div>
      </section>
      {save.isError ? <ErrorBox error={save.error} /> : null}
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="ghost" size="lg" onClick={onClose}>
          {ro ? 'Закрыть' : 'Отмена'}
        </Button>
        {ro ? null : (
          <Button variant="primary" size="lg" loading={save.isPending} onClick={() => save.mutate()}>
            Сохранить
          </Button>
        )}
      </div>
    </div>
  );
}
